import { Link } from "react-router-dom";
import { UserPlus, UserMinus, ShieldOff, ShieldBan, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, BACKEND } from "../lib/api";
import { short } from "../lib/format";
import { useApp } from "../context/AppContext";

export const Avatar = ({ url, address, size = 32, className = "" }) => (
  url ? <img src={`${BACKEND}${url}`} alt="" style={{ width: size, height: size }} className={`rounded-full object-cover ring-1 ring-lime/30 shrink-0 ${className}`} />
    : <div style={{ width: size, height: size, background: `hsl(${parseInt((address || "0x00").slice(2, 8), 16) % 360} 30% 18%)` }}
      className={`hex shrink-0 grid place-items-center text-[10px] font-bold text-lime ${className}`}>{(address || "").slice(2, 4).toUpperCase()}</div>
);

export const useRelations = () => {
  const { user, social, refreshSocial } = useApp();
  const call = async (method, path, msg, after) => {
    if (!user) return toast.error("Connect your wallet and sign in first");
    try { await api[method](path); toast.success(msg); refreshSocial(); after?.(); } catch (e) { toast.error(errMsg(e)); }
  };
  return {
    isFriend: (a) => social.friends.has(a), isBlocked: (a) => social.blocked.has(a),
    addFriend: (a, after) => call("post", `/friends/${a}`, "Added to friends", after),
    removeFriend: (a, after) => call("delete", `/friends/${a}`, "Removed from friends", after),
    block: (a, after) => call("post", `/blocks/${a}`, "Member blocked — their messages are hidden for you", after),
    unblock: (a, after) => call("delete", `/blocks/${a}`, "Member unblocked", after),
  };
};

export const RelationButtons = ({ address, onChange, size = "sm" }) => {
  const { user } = useApp();
  const r = useRelations();
  if (!user || user.address === address) return null;
  const cls = `inline-flex items-center gap-1.5 rounded-lg border font-semibold transition-colors ${size === "sm" ? "px-2.5 py-1 text-[11px]" : "px-4 py-2 text-xs"}`;
  const blocked = r.isBlocked(address);
  return (
    <div className="flex flex-wrap gap-2">
      {!blocked && (r.isFriend(address)
        ? <button data-testid={`member-unfriend-${address}`} onClick={() => r.removeFriend(address, onChange)} className={`${cls} border-lime/40 text-lime bg-lime/10 hover:bg-lime/20`}><UserMinus className="h-3.5 w-3.5" /> Friends</button>
        : <button data-testid={`member-add-friend-${address}`} onClick={() => r.addFriend(address, onChange)} className={`${cls} border-lime/40 text-lime hover:bg-lime/10`}><UserPlus className="h-3.5 w-3.5" /> Add friend</button>)}
      {blocked
        ? <button data-testid={`member-unblock-${address}`} onClick={() => r.unblock(address, onChange)} className={`${cls} border-white/20 text-slate-300 hover:bg-white/5`}><ShieldOff className="h-3.5 w-3.5" /> Unblock</button>
        : <button data-testid={`member-block-${address}`} onClick={() => r.block(address, onChange)} className={`${cls} border-danger/40 text-danger hover:bg-danger/10`}><ShieldBan className="h-3.5 w-3.5" /> Block</button>}
    </div>
  );
};

export const MemberRow = ({ m, onChange }) => (
  <div data-testid={`member-row-${m.address}`} className="flex items-center gap-3 rounded-xl px-3 py-3 hover:bg-lime/[0.04] border border-transparent hover:border-lime/10">
    <Link to={`/u/${m.address}`}><Avatar url={m.avatar_url} address={m.address} size={42} /></Link>
    <div className="flex-1 min-w-0">
      <Link to={`/u/${m.address}`} className="flex items-center gap-1.5 font-semibold text-white hover:text-lime truncate">
        {m.nickname || short(m.address)} {m.is_admin && <ShieldCheck className="h-3.5 w-3.5 text-lime" />}
      </Link>
      <p className="text-[11px] font-mono text-slate-500">{short(m.address, 6)}</p>
      {m.bio && <p className="text-xs text-slate-400 truncate">{m.bio}</p>}
    </div>
    <RelationButtons address={m.address} onChange={onChange} />
  </div>
);
